import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { allSurveys } from './data';
import analyzers from './analyzers';
import { useTranslation } from 'react-i18next';
import SurveyIntro from './SurveyIntro';
import SurveyQuestions from './SurveyQuestions';
import SurveyResults from './SurveyResults';

function SurveyContainer() {
    const { i18n } = useTranslation();
    const { testSlug } = useParams();

    const survey = allSurveys[testSlug];
    const lang = localStorage.getItem('lang') || i18n.language || 'en';

    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem(`survey_answers_${testSlug}`);
        return saved ? JSON.parse(saved) : {};
    });

    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = localStorage.getItem(`survey_page_${testSlug}`);
        return savedPage ? parseInt(savedPage, 10) : 0;
    });

    const [result, setResult] = useState(() => {
        const savedResult = localStorage.getItem(`survey_result_${testSlug}`);
        return savedResult ? JSON.parse(savedResult) : null;
    });

    const [showIntro, setShowIntro] = useState(() => {
        const savedAnswers = localStorage.getItem(`survey_answers_${testSlug}`);
        const savedResult = localStorage.getItem(`survey_result_${testSlug}`);
        const hasAnswers = savedAnswers && Object.keys(JSON.parse(savedAnswers)).length > 0;
        return !hasAnswers && !savedResult;
    });

    const questions = survey.questions;
    const currentQuestion = questions[currentPage];
    const isLastQuestion = currentPage === questions.length - 1;

    useEffect(() => {
        const savedAnswers = localStorage.getItem(`survey_answers_${testSlug}`);
        const savedPage = localStorage.getItem(`survey_page_${testSlug}`);
        const savedResult = localStorage.getItem(`survey_result_${testSlug}`);

        const parsedAnswers = savedAnswers ? JSON.parse(savedAnswers) : {};
        const parsedResult = savedResult ? JSON.parse(savedResult) : null;

        setAnswers(parsedAnswers);
        setCurrentPage(savedPage ? parseInt(savedPage, 10) : 0);
        setResult(parsedResult);

        const hasAnswers = Object.keys(parsedAnswers).length > 0;
        setShowIntro(!hasAnswers && !parsedResult);
    }, [testSlug]);

    useEffect(() => {
        if (Object.keys(answers).length > 0) {
            localStorage.setItem(`survey_answers_${testSlug}`, JSON.stringify(answers));
        }
        localStorage.setItem(`survey_page_${testSlug}`, currentPage.toString());
    }, [answers, currentPage, testSlug]);

    const handleStart = () => {
        setShowIntro(false);
    };

    // 1. Just select option & save to state (no navigation)
    const handleSelectOption = (questionOrder, value) => {
        setAnswers(prev => ({ ...prev, [questionOrder]: value }));
    };

    // 2. Advance page or calculate final score when Next is clicked
    const handleNext = () => {
        if (!isLastQuestion) {
            setCurrentPage(prev => prev + 1);
        } else {
            const analysis = analyzers[testSlug](answers);
            setResult(analysis);

            localStorage.setItem(`survey_result_${testSlug}`, JSON.stringify(analysis));
            localStorage.removeItem(`survey_answers_${testSlug}`);
            localStorage.removeItem(`survey_page_${testSlug}`);
        }
    };

    // 3. Move back one page
    const handlePrev = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleReset = () => {
        localStorage.removeItem(`survey_result_${testSlug}`);
        localStorage.removeItem(`survey_answers_${testSlug}`);
        localStorage.removeItem(`survey_page_${testSlug}`);
        setResult(null);
        setAnswers({});
        setCurrentPage(0);
        setShowIntro(true);
    };

    return (
        <div className="survey-wrapper">
            {result ? (
                <SurveyResults
                    result={result}
                    survey={survey}
                    lang={lang}
                    onReset={handleReset}
                />
            ) : showIntro ? (
                <SurveyIntro
                    survey={survey}
                    lang={lang}
                    onStart={handleStart}
                />
            ) : (
                <SurveyQuestions
                    questions={questions}
                    currentPage={currentPage}
                    currentQuestion={currentQuestion}
                    answerOptions={survey.answerOptions}
                    answers={answers}
                    lang={lang}
                    onSelectOption={handleSelectOption}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    isFirstPage={currentPage === 0}
                />
            )}
        </div>
    );
}

export default SurveyContainer;